import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// App icons (inline SVG so they inherit the accent colour)
const AppIcon = ({ kind, color, size = 30 }) => {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none' };
  if (kind === 'pm') return (
    <svg {...common}><rect x="3" y="4" width="18" height="16" rx="2" stroke={color} strokeWidth="1.7" />
      <path d="M3 9h18M8 4v16" stroke={color} strokeWidth="1.7" /><circle cx="15.5" cy="13" r="1.4" fill={color} /></svg>
  );
  if (kind === 'crm') return (
    <svg {...common}><circle cx="9" cy="8" r="3" stroke={color} strokeWidth="1.7" />
      <path d="M3.5 20a5.5 5.5 0 0111 0M16 6.5a3 3 0 010 6M17.5 20a5.5 5.5 0 00-3-4.9" stroke={color} strokeWidth="1.7" strokeLinecap="round" /></svg>
  );
  if (kind === 'profile') return (
    <svg {...common}><circle cx="12" cy="8" r="3.5" stroke={color} strokeWidth="1.7" />
      <path d="M5 19.5a7 7 0 0114 0" stroke={color} strokeWidth="1.7" strokeLinecap="round" /></svg>
  );
  if (kind === 'admin') return (
    <svg {...common}><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M9 12l2 2 4-4" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
  );
  if (kind === 'ipp') return (
    <svg {...common}><path d="M7 3h7l4 4v14a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M13 3v5h5M9 13l1.5 1.5L14 11M9 17h6" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
  );
  return (
    <svg {...common}><rect x="4" y="4" width="7" height="7" rx="1.5" stroke={color} strokeWidth="1.7" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" stroke={color} strokeWidth="1.7" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" stroke={color} strokeWidth="1.7" />
      <rect x="13" y="13" width="7" height="7" rx="1.5" stroke={color} strokeWidth="1.7" /></svg>
  );
};

function hexToRgba(hex, a) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16), g = parseInt(h.substring(2, 4), 16), b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

function AppCard({ app, onOpen }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={() => onOpen(app)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative', textAlign: 'left', cursor: 'pointer', background: 'var(--surface)',
        border: `1px solid ${hover ? app.accent : 'var(--border)'}`,
        borderRadius: 18, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: hover ? `0 16px 38px ${hexToRgba(app.accent, 0.20)}` : '0 1px 3px rgba(16,24,40,0.05)',
        transform: hover ? 'translateY(-4px)' : 'none', transition: 'all .2s ease', minHeight: 196,
      }}>
      <div style={{ height: 5, background: `linear-gradient(90deg, ${app.accent}, ${hexToRgba(app.accent, 0.55)})` }} />
      <div style={{ padding: '22px 22px 20px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{
            width: 54, height: 54, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: hexToRgba(app.accent, 0.10), border: `1px solid ${hexToRgba(app.accent, 0.22)}`,
          }}>
            <AppIcon kind={app.icon} color={app.accent} />
          </div>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase',
            color: app.accent, background: hexToRgba(app.accent, 0.10), padding: '4px 9px', borderRadius: 999,
          }}>{app.badge || 'Available'}</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.3px', marginBottom: 6 }}>{app.name}</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted2)', lineHeight: 1.55 }}>{app.description}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: app.accent }}>
          Open
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ transform: hover ? 'translateX(4px)' : 'none', transition: 'transform .2s' }}>
            <path d="M3 8h9M9 4.5L12.5 8 9 11.5" stroke={app.accent} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
      </div>
    </button>
  );
}

export default function AppHubPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [apps, setApps] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/auth/my-apps')
      .then(d => { if (alive) setApps(Array.isArray(d?.apps) ? d.apps : []); })
      .catch(() => { if (alive) { setApps([]); setError('Could not load your applications.'); } });
    return () => { alive = false; };
  }, []);

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  })();
  const firstName = (user?.full_name || '').split(' ')[0] || '';
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <header style={{
        height: 62, flexShrink: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', padding: '0 26px', gap: 12,
      }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#5B5BD6,#7C3AED)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: '#fff', fontSize: 17, letterSpacing: '-0.5px', boxShadow: '0 2px 10px rgba(91,91,214,0.4)' }}>E</div>
        <div style={{ lineHeight: 1.15 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.4px' }}>EOMP</div>
          <div style={{ fontSize: 10, color: 'var(--muted)', letterSpacing: '0.3px' }}>Enterprise Operations Management Platform</div>
        </div>
        <img src="/sct-logo.png" alt="SCT" style={{ height: 30, opacity: 0.7, marginLeft: 8 }} title="Space Communication Technologies" />
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%', background: user?.avatar_color || '#5B5BD6',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700,
          }}>{(user?.full_name || '?').charAt(0).toUpperCase()}</div>
          <span style={{ fontSize: 12.5, color: 'var(--muted2)', fontWeight: 600 }}>{user?.full_name}</span>
          {user?.role !== 'superadmin' && (
            <button onClick={() => navigate('/profile')} title="My information used across apps"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--muted2)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M2.5 14a5.5 5.5 0 0111 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              My Information
            </button>
          )}
          <button onClick={() => { logout(); navigate('/login'); }} title="Sign out"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--muted2)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 14H3V2h3M10 11l3-3-3-3M13 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Sign out
          </button>
        </div>
      </header>

      <div style={{ background: 'linear-gradient(120deg, #2A2550 0%, #4B3F8F 55%, #5B5BD6 100%)', color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -90, right: -60, width: 320, height: 320, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ position: 'absolute', bottom: -120, left: '30%', width: 280, height: 280, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ maxWidth: 'min(94vw, 1480px)', margin: '0 auto', padding: '34px clamp(20px, 2.5vw, 40px) 38px', position: 'relative' }}>
          <div style={{ fontSize: 12.5, opacity: 0.7, fontWeight: 600, marginBottom: 6 }}>{today}</div>
          <h1 style={{ fontSize: 28, fontWeight: 850, letterSpacing: '-0.7px', margin: '0 0 8px' }}>{greeting}{firstName ? `, ${firstName}` : ''}</h1>
          <p style={{ fontSize: 14, opacity: 0.8, margin: 0, maxWidth: 560, lineHeight: 1.5 }}>
            Welcome to your operations workspace. Select an application below to get started — you'll only see the ones you've been given access to.
          </p>
        </div>
      </div>

      <main style={{ flex: 1, overflow: 'auto', padding: '34px 26px 60px' }}>
        <div style={{ maxWidth: 'min(94vw, 1480px)', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <h2 style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.2px', margin: 0 }}>Your applications</h2>
            {Array.isArray(apps) && apps.length > 0 && (
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{apps.length} {apps.length === 1 ? 'application' : 'applications'} available</span>
            )}
          </div>

          {apps === null ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--muted)', fontSize: 13, padding: '30px 0' }}>
              <div style={{ width: 16, height: 16, border: '2px solid var(--border)', borderTopColor: 'var(--brand)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              Loading your applications…
            </div>
          ) : (apps.length === 0 && user?.role === 'superadmin') ? (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '40px', textAlign: 'center', color: 'var(--muted2)' }}>
              <div style={{ width: 52, height: 52, borderRadius: 13, background: 'var(--surface2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <AppIcon kind="generic" color="#8A93A6" size={26} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>No applications assigned yet</div>
              <div style={{ fontSize: 13, maxWidth: 420, margin: '0 auto', lineHeight: 1.5 }}>{error || "You haven't been added to any project or given CRM access. Please contact your administrator to be granted access."}</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 20 }}>
              {(user?.role !== 'superadmin'
                ? [{ key: 'myinfo', name: 'My Information', description: 'Your job title, department, Performance-Plan details and signature — set once, used across all apps.', path: '/profile', accent: '#5B5BD6', icon: 'profile', badge: 'Profile' }, ...apps]
                : apps
              ).map(app => <AppCard key={app.key} app={app} onOpen={a => navigate(a.path)} />)}
            </div>
          )}

          <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 10 }}>
            <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>© 2026 Space Communication Technologies · Enterprise Operations Management Platform</span>
          </div>
        </div>
      </main>
    </div>
  );
}
